import { useEffect, useMemo } from 'react';
import { Modal, Form, DatePicker, Select, Input, InputNumber, Alert, AutoComplete, message } from 'antd';
import dayjs from 'dayjs';
import { APPOINTMENT_STATUS, SERVICE_PRICES, callCenterApi, servicePrice, useCallCenter, type Customer } from '../callCenterStore';
import { meName } from '../softphone';

const uniq = (xs: string[]) => Array.from(new Set(xs.filter(Boolean))).map(v => ({ value: v, label: v }));

/** Đặt lịch hẹn nhanh ngay trong hồ sơ khách (front-end, sau này nối API lịch hẹn) */
export default function AppointmentModal({ customer, open, onClose }: { customer: Customer; open: boolean; onClose: () => void }) {
  const s = useCallCenter();
  const [form] = Form.useForm();
  const at = Form.useWatch('at', form);
  const services = useMemo(() => uniq([...Object.keys(SERVICE_PRICES), ...s.customers.map(c => c.service)]), [s.customers]);
  const branches = useMemo(() => uniq(s.customers.map(c => c.branch)), [s.customers]);
  // Bác sĩ / KTV đã từng thực hiện — gợi ý nhập nhanh
  const practitioners = useMemo(() => uniq(['BS. Lan Anh', 'BS. Minh Quân', 'KTV Thu Hà', ...s.appointments.map(a => a.practitioner || '')]), [s.appointments]);

  useEffect(() => {
    if (open) form.setFieldsValue({ at: null, service: customer.service || undefined, branch: customer.branch || undefined, note: '', practitioner: undefined, price: customer.service ? servicePrice(customer.service) || null : null });
  }, [open, customer, form]);

  // Khách đã có lịch đang hiệu lực trong cùng ngày → cảnh báo trùng
  const sameDay = at
    ? s.appointments.filter(a => a.customerId === customer.id && (a.status === 'new' || a.status === 'confirmed') && dayjs(a.at).isSame(at, 'day'))
    : [];

  const submit = async () => {
    const v = await form.validateFields();
    callCenterApi.addAppointment({ customerId: customer.id, at: v.at.valueOf(), service: v.service, branch: v.branch, note: v.note?.trim() || undefined, price: v.price ?? undefined, practitioner: v.practitioner?.trim() || undefined, by: meName() });
    message.success(`Đã đặt lịch ${dayjs(v.at).format('HH:mm DD/MM/YYYY')} cho ${customer.name}`);
    onClose();
  };

  return (
    <Modal open={open} title={`Tạo lịch hẹn · ${customer.name}`} onCancel={onClose} onOk={submit} okText="Đặt lịch" cancelText="Huỷ" destroyOnClose>
      <Form form={form} layout="vertical" className="pt-2" onValuesChange={ch => { if (ch.service) form.setFieldValue('price', servicePrice(ch.service) || null); }}>
        {customer.medicalNote && <Alert type="error" showIcon className="!mb-4" message={<><b>Lưu ý y khoa:</b> {customer.medicalNote}</>} />}
        <Form.Item name="at" label="Thời gian hẹn" rules={[
          { required: true, message: 'Chọn thời gian hẹn' },
          { validator: (_, v) => (!v || v.isAfter(dayjs()) ? Promise.resolve() : Promise.reject(new Error('Thời gian hẹn phải ở tương lai'))) },
        ]}>
          <DatePicker showTime={{ format: 'HH:mm', minuteStep: 15 }} format="HH:mm DD/MM/YYYY" className="w-full" disabledDate={d => d.isBefore(dayjs(), 'day')}
            presets={[
              { label: 'Sáng mai 9:00', value: dayjs().add(1, 'day').hour(9).minute(0).second(0) },
              { label: 'Chiều mai 14:00', value: dayjs().add(1, 'day').hour(14).minute(0).second(0) },
              { label: 'Cuối tuần 10:00', value: dayjs().day(6).isAfter(dayjs()) ? dayjs().day(6).hour(10).minute(0).second(0) : dayjs().add(1, 'week').day(6).hour(10).minute(0).second(0) },
            ]} />
        </Form.Item>
        {sameDay.length > 0 && (
          <Alert type="warning" showIcon className="!mb-4"
            message={`Khách đã có ${sameDay.length} lịch trong ngày này: ${sameDay.map(a => `${dayjs(a.at).format('HH:mm')} ${a.service} (${APPOINTMENT_STATUS[a.status].label})`).join(', ')}`} />
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
          <Form.Item name="service" label="Dịch vụ" rules={[{ required: true, message: 'Chọn dịch vụ' }]}>
            <Select showSearch options={services} placeholder="Chọn dịch vụ" />
          </Form.Item>
          <Form.Item name="branch" label="Chi nhánh" rules={[{ required: true, message: 'Chọn chi nhánh' }]}>
            <Select options={branches} placeholder="Chọn chi nhánh" />
          </Form.Item>
        </div>
        <Form.Item name="practitioner" label="Bác sĩ / KTV thực hiện" extra="Dùng để khảo sát hài lòng theo người thực hiện và báo bất thường sau dịch vụ đúng người.">
          <AutoComplete options={practitioners} placeholder="Chọn hoặc nhập tên" filterOption={(i, o) => String(o?.value || '').toLowerCase().includes(i.toLowerCase())} />
        </Form.Item>
        <Form.Item name="price" label="Giá trị dịch vụ (đ)" extra={Number((customer.csStage || 'S0').slice(1)) < 4 ? 'Đặt lịch xong, Trạng thái CS của khách sẽ tự chuyển sang S4.' : undefined}>
          <InputNumber<number> className="!w-full" min={0} step={500000} formatter={v => (v ? Number(v).toLocaleString('vi-VN') : '')} parser={v => Number(String(v || '').replace(/\D/g, ''))} />
        </Form.Item>
        <Form.Item name="note" label="Ghi chú">
          <Input.TextArea autoSize={{ minRows: 2 }} placeholder="Yêu cầu của khách, người đi cùng..." />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export function AppointmentStatusBadge({ status }: { status: keyof typeof APPOINTMENT_STATUS }) {
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${APPOINTMENT_STATUS[status].badge}`}>{APPOINTMENT_STATUS[status].label}</span>;
}

